import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, TextField, IconButton, ToggleButtonGroup, ToggleButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import JsonTree from './JsonTree';
import PinnedList from './PinnedList';
import useSplitPanel from './hooks/useSplitPanel';
import AddNodeInline from './AddNodeInline';

const PageView = ({
  title,
  searchTerm,
  onSearchChange,
  onReload,
  treeData,
  onEdit,
  supportsTouch,
  pinnedEntries,
  onUnpin,
  onTogglePin,
  pinnedPathSet,
  onAddProperty,
  activeInlineAddPath,
  onInlineAddPathChange,
  onAddNode,
  priorityMap = {},
  exactMatchOnly = false,
  onExactMatchToggle
}) => {
  const { ratio: mainRatio, startResizing, attachContainer } = useSplitPanel({ initialRatio: 0.7, minRatio: 0.35, maxRatio: 0.85 });
  const [inlineAddVisible, setInlineAddVisible] = useState(false);
  const topLevelKeys = useMemo(() => {
    if (!treeData || typeof treeData !== 'object' || Array.isArray(treeData)) return [];
    return Object.keys(treeData);
  }, [treeData]);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: 2, height: 'calc(100vh - 64px)', boxSizing: 'border-box', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Button onClick={onReload} size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: 1 }}>
          Load current CLTStatus
        </Button>
        {onAddNode && (
          <IconButton
            size="small"
            onClick={() => setInlineAddVisible((prev) => !prev)}
            sx={(theme) => ({
              borderRadius: 1,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            })}
            aria-label="Add node">
            <AddIcon fontSize="small" />
          </IconButton>
        )}
        <TextField
          value={searchTerm}
          onChange={onSearchChange}
          placeholder="Search keys & values..."
          size="small"
          variant="outlined"
          inputProps={{ 'aria-label': 'Search JSON' }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              height: 32,
              alignItems: 'center',
              display: 'flex',
              borderRadius: 14,
              '& input': {
                padding: '6px 12px',
                margin: 0,
                lineHeight: 1.2
              },
              '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                top: 0,
                bottom: 0,
                borderRadius: 14
              },
              '& legend': {
                display: 'none'
              },
              '&.Mui-focused fieldset': {
                borderWidth: 1.5,
                borderColor: '#1976d2'
              }
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
              color: '#1976d2'
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 1
            }
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={exactMatchOnly ? 'exact' : 'contains'}
          onChange={(_, next) => {
            if (!next) return;
            onExactMatchToggle?.(next === 'exact');
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              width: 80,
              px: 1.25,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: (theme) => theme.palette.divider,
              color: (theme) => theme.palette.text.secondary
            },
            '& .MuiToggleButton-root.Mui-selected': {
              borderColor: (theme) => theme.palette.primary.main,
              backgroundColor: (theme) => theme.palette.action.hover,
              color: (theme) => theme.palette.primary.main
            }
          }}>
          <ToggleButton value="contains">Contains</ToggleButton>
          <ToggleButton value="exact">Exact</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {onAddNode && inlineAddVisible && <AddNodeInline visible variant="page" onCancel={() => setInlineAddVisible(false)} onCreate={onAddNode} existingKeys={topLevelKeys} />}

      <Box ref={attachContainer} sx={{ flexGrow: 1, display: 'flex', gap: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            flexGrow: mainRatio,
            overflow: 'auto',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            padding: 1,
            backgroundColor: (theme) => theme.palette.background.paper
          }}>
          <JsonTree
            data={treeData}
            searchTerm={searchTerm}
            onEdit={onEdit}
            appearance="page"
            supportsTouch={supportsTouch}
            onTogglePin={onTogglePin}
            pinnedPathSet={pinnedPathSet}
            onAddProperty={onAddProperty}
            activeInlineAddPath={activeInlineAddPath}
            onInlineAddPathChange={onInlineAddPathChange}
            priorityMap={priorityMap}
            exactMatchOnly={exactMatchOnly}
          />
        </Box>
        <Box
          onPointerDown={startResizing}
          sx={{
            width: 10,
            cursor: 'col-resize',
            backgroundColor: (theme) => theme.palette.divider,
            borderRadius: 999,
            alignSelf: 'stretch',
            flexShrink: 0,
            touchAction: 'none'
          }}
        />
        <Box
          sx={{
            flexGrow: 1 - mainRatio,
            minWidth: 240,
            display: 'flex'
          }}>
          <PinnedList
            entries={pinnedEntries}
            onUnpin={onUnpin}
            onEdit={onEdit}
            onTogglePin={onTogglePin}
            pinnedPathSet={pinnedPathSet}
            variant="page"
            sx={{ width: '100%' }}
            onAddProperty={onAddProperty}
            activeInlineAddPath={activeInlineAddPath}
            onInlineAddPathChange={onInlineAddPathChange}
            priorityMap={priorityMap}
            exactMatchOnly={exactMatchOnly}
          />
        </Box>
      </Box>
    </Box>
  );
};

PageView.propTypes = {
  title: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  treeData: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.oneOf([null])]),
  onEdit: PropTypes.func.isRequired,
  supportsTouch: PropTypes.bool,
  pinnedEntries: PropTypes.arrayOf(
    PropTypes.shape({
      pathKey: PropTypes.string.isRequired,
      keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  onUnpin: PropTypes.func,
  onTogglePin: PropTypes.func,
  pinnedPathSet: PropTypes.instanceOf(Set),
  onAddProperty: PropTypes.func,
  activeInlineAddPath: PropTypes.string,
  onInlineAddPathChange: PropTypes.func,
  onAddNode: PropTypes.func,
  priorityMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  exactMatchOnly: PropTypes.bool,
  onExactMatchToggle: PropTypes.func
};

export default PageView;
